import { InputHTMLAttributes, forwardRef, useEffect, useRef } from 'react';

import Button, { ButtonProps } from '@components/buttons/Button';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  focusOnMount?: boolean;
  variant?: 'outlined' | 'default';
  color?: 'default' | 'neutral';
  wrapperClassName?: string;
  name?: string;
  submitButton?: Omit<ButtonProps, 'children'> & { text: string };
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      focusOnMount,
      variant = 'default',
      wrapperClassName = '',
      submitButton,
      ...props
    },
    forwardedRef,
  ) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const ref = forwardedRef ?? internalRef;

    useEffect(() => {
      if (focusOnMount) {
        if (internalRef.current) {
          internalRef.current.focus();
        } else {
          // Check if forwardedRef is a function or an object
          if (typeof forwardedRef === 'function') {
            // If it's a function, call it with the current value
            forwardedRef(internalRef.current);
          } else if (forwardedRef && 'current' in forwardedRef) {
            // If it's an object with a 'current' property, access it directly
            forwardedRef.current?.focus();
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const element = (
      <div className={`input input-variant-${variant} ${wrapperClassName}`}>
        <input ref={ref} type={type} {...props} />
        {submitButton && <Button {...submitButton} />}
      </div>
    );

    return element;
  },
);
